import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, ModalFeedback, SparkIcon, SparkModalPortal, Spinner, Text } from '@spark';

import ModelProviders from '@/features/settings/components/ModelProviders.jsx';
import { useOrganizationProviderSettings } from '@/features/settings/hooks/useOrganizationProviderSettings.js';
import '@/features/settings/components/settings.css';

const EMPTY_DRAFT_STATE = { hasDraft: false, isValidating: false };

/**
 * First-time onboarding: Model Providers modal (Figma 3236-20483 / 3236-21060).
 * Skip opens a warning; Continue is enabled once ≥1 credential is saved or an
 * API key has been typed (not yet confirmed with Enter). Clicking Continue with
 * an unconfirmed key runs the usual validate-and-save flow — showing a spinning
 * loader on the button — and advances only when the key is valid.
 * X dismisses and stays on Projects (no warning).
 *
 * The providers portal stays mounted (hidden) while the skip warning is open so
 * the backdrop never flashes the empty Projects page between the two dialogs.
 */
const ModelProvidersOnboardingModal = ({
    open,
    onClose,
    onContinue,
}) => {
    const [skipWarningOpen, setSkipWarningOpen] = useState(false);
    const [draftState, setDraftState] = useState(EMPTY_DRAFT_STATE);
    const [isContinueValidating, setIsContinueValidating] = useState(false);
    const vendorDraftSubmitRef = useRef(null);
    const {
        settings,
        isLoading,
        hasCredentials,
        updateVendorKeys,
        updateRecommendedProvider,
        updateBedrockCredentials,
        clearBedrockCredentials,
        updateVertexCredentials,
        clearVertexCredentials,
        testVertexConnection,
        updateAzureCredentials,
        clearAzureCredentials,
    } = useOrganizationProviderSettings({ autoFetch: open });

    useEffect(() => {
        if (!open) {
            setSkipWarningOpen(false);
            setDraftState(EMPTY_DRAFT_STATE);
            setIsContinueValidating(false);
        }
    }, [open]);

    // A key typed into the input (before Enter) or an in-flight Enter
    // validation both count towards enabling Continue.
    const canContinue = hasCredentials || draftState.hasDraft || draftState.isValidating;

    const handleContinue = useCallback(async () => {
        if (isContinueValidating) return;

        const submitDraft = vendorDraftSubmitRef.current;
        if ((draftState.hasDraft || draftState.isValidating) && submitDraft) {
            // Validate-and-save the unconfirmed key (or await the validation
            // the user already started with Enter), then advance on success.
            setIsContinueValidating(true);
            try {
                const added = await submitDraft();
                if (added) onContinue();
            } finally {
                setIsContinueValidating(false);
            }
            return;
        }
        if (hasCredentials) {
            onContinue();
        }
    }, [isContinueValidating, draftState, hasCredentials, onContinue]);

    if (!open) return null;

    const handleSkip = () => {
        setSkipWarningOpen(true);
    };

    const handleSkipCancel = () => {
        setSkipWarningOpen(false);
    };

    const handleSkipConfirm = () => {
        setSkipWarningOpen(false);
        onContinue();
    };

    const handleClose = () => {
        setSkipWarningOpen(false);
        onClose();
    };

    return (
        <>
            <SparkModalPortal
                open={open}
                onClose={handleClose}
                size="wide"
                mask={!skipWarningOpen}
                keyboard={!skipWarningOpen}
                hidden={skipWarningOpen}
                destroyOnClose
                className="model-providers-onboarding-modal-portal"
            >
                <Modal
                    title="Model Providers"
                    showSubtitle={false}
                    showScrollBar
                    showOverlay={false}
                    fixedHeight={false}
                    showClose={!skipWarningOpen}
                    primaryLabel="Continue"
                    tertiaryLabel="Skip"
                    primaryDisabled={!canContinue}
                    primaryDisableWhenReadOnly
                    primaryIcon={
                        isContinueValidating ? (
                            <SparkIcon
                                slug="loader"
                                size="small"
                                className="model-providers-onboarding-modal__continue-loader"
                            />
                        ) : undefined
                    }
                    onPrimaryClick={handleContinue}
                    onTertiaryClick={handleSkip}
                    onCloseClick={handleClose}
                    className="model-providers-onboarding-modal"
                >
                    {isLoading ? (
                        <div className="settings-card settings-card--loading">
                            <Spinner size="medium" />
                            <Text variant="base-regular" color="secondary">
                                Loading settings...
                            </Text>
                        </div>
                    ) : (
                        <ModelProviders
                            embedded
                            settings={settings}
                            onVendorDraftStateChange={setDraftState}
                            vendorDraftSubmitRef={vendorDraftSubmitRef}
                            onVendorKeysUpdate={updateVendorKeys}
                            onRecommendedProviderUpdate={updateRecommendedProvider}
                            onBedrockUpdate={updateBedrockCredentials}
                            onBedrockClear={clearBedrockCredentials}
                            onVertexUpdate={updateVertexCredentials}
                            onVertexClear={clearVertexCredentials}
                            onVertexTestConnection={testVertexConnection}
                            onAzureUpdate={updateAzureCredentials}
                            onAzureClear={clearAzureCredentials}
                            platformCloudProvider={settings.platform_cloud_provider}
                        />
                    )}
                </Modal>
            </SparkModalPortal>

            <SparkModalPortal
                open={skipWarningOpen}
                onClose={handleSkipCancel}
                size="text"
                destroyOnClose
                className="model-providers-skip-warning-portal"
            >
                <ModalFeedback
                    feedbackType="warning"
                    title="Continue with no model provider configured?"
                    content="Without model provider credentials, teacher-model comparison and related features won't be available. Are you sure you want to continue?"
                    showOverlay={false}
                    primaryLabel="Continue anyway"
                    tertiaryLabel="Cancel"
                    primaryDisableWhenReadOnly
                    onPrimaryClick={handleSkipConfirm}
                    onTertiaryClick={handleSkipCancel}
                    onCloseClick={handleSkipCancel}
                    className="model-providers-skip-warning"
                />
            </SparkModalPortal>
        </>
    );
};

export default ModelProvidersOnboardingModal;
